import styles from './StudyFilesSection.module.scss';
import { useCallback, useState, FC, useEffect, useMemo } from 'react';
import { ReactComponent as AddIcon } from 'assets/img/common/plus_24.svg';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { ConfirmResult, useConfirmationModal } from 'src/components/dialogModal/useConfirmationModal';
import { StudyInfoPageSection } from '../../layoutComponents/StudyInfoPageLayout';
import { AppButtonWithTooltip } from 'uikit/buttons/buttonWithTooltip/buttonWithTooltip';
import { downloadFile, formatFileSize } from 'src/helpers/file-helper';
import { removeAttachmentFromStudy } from 'src/services/api/api-client/StudyClient';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { getStudyAttachmentsQueryKey, useGetStudyAttachmentsQuery } from 'src/services/api/api-client/StudyQuery';
import { useQueryClient } from '@tanstack/react-query';
import { IStudyAttachmentDto } from 'src/services/api/api-client';
import { ReactComponent as FileIcon } from 'src/assets/img/common/file_20.svg';
import { ReactComponent as CrossIcon } from 'src/assets/img/common/b_cross_24.svg';
import { ReactComponent as NewCommentIcon } from 'assets/img/notification/report_filled_20.svg';
import { ReactComponent as PlaceholderIcon } from 'assets/img/common/il_g_clipboard_attachments_196.svg';
import { Trans } from 'react-i18next';
import { getFileById } from 'src/services/api/api-client/FilesClient';
import { logger } from 'src/application/logging/logging';
import { showNotificationToast } from 'src/components/toast/toast-helper';
import { AppProgress } from 'uikit/progress/AppProgress';
import { useFileUploader } from './useFileUploader';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { useModal } from 'src/application/hooks/useModal';
import { AppAttachmentsV2 } from 'uikit/appAttachmentsV2/AppAttachmentsV2';
import { AllowedExtensionsType, AppAttachmentValue } from 'uikit/appAttachmentsV2/AppAttachmentsV2.d';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { mergeValueWithError, parseError, validateFileRule } from 'uikit/appAttachmentsV2/Hook-form-helpers';
import Lottie from 'react-lottie-player';
import ProgressAnimation from 'src/assets/animations/loader_20/progress.json';

// NOTE: Don't forget to sync this value with the backend StudyController.AddAttachmentToStudy() method
const FILE_SIZE_LIMIT_MB = 50;
const FILE_TYPES = [
  '.txt',
  '.pdf',
  '.doc',
  '.jpeg',
  '.jpg',
  '.zip',
  '.7z',
  '.png',
  '.xlsx',
  '.xlx',
  '.docx',
  '.csv',
  '.pdat',
  '.psdat',
  '.pvdat',
  '.ldat',
] as AllowedExtensionsType[];

export const StudyFilesSection = () => {
  const { id } = useStudy() ?? {};
  const { t } = useScopedTranslation('StudyInfoPage.StudyFilesSection');
  const { isInitialLoading, data } = useGetStudyAttachmentsQuery({ studyId: id! });
  const { progress } = useFileUploader();

  // NOTE: Prevent close browser tab if upload is in progress
  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if (progress.percents > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [progress.percents]);

  return (
    <StudyInfoPageSection
      title={t('Title')}
      subTitle={t('SubTitle')}
      rightHeader={<StudyFilesUploadingComponent />}
      isFullHeight={true}
      variant={'block'}
      isLoading={isInitialLoading}
      placeholderProps={{
        Icon: PlaceholderIcon,
        text: t('Placeholder'),
        showPlaceholder: !data?.length && !progress.files.length,
      }}
    >
      {progress.percents > 0 &&
        progress.files.map((file) => (
          <FileComponent
            key={file.fileName}
            fileDto={{ fileId: null, fileName: file.fileName, fileSizeBytes: file.fileSize }}
            isUploading={true}
            uploadPercents={progress.percents}
          />
        ))}
      {data?.map((x) => (
        <FileComponent key={x.fileId} fileDto={x} />
      ))}
    </StudyInfoPageSection>
  );
};

const FileComponent: FC<{
  fileDto: {
    fileId: string | null;
    fileName: string;
    fileSizeBytes: number;
  };
  isUploading?: boolean;
  uploadPercents?: number;
}> = ({ fileDto, isUploading, uploadPercents }) => {
  const client = useQueryClient();
  const { id } = useStudy() ?? {};
  const { t } = useScopedTranslation('StudyInfoPage.StudyFilesSection');
  const { modal, open } = useConfirmationModal();
  const [downloadingPercents, setDownloadingPercents] = useState<number>();
  const [deleting, setDeleting] = useState(false);

  const downloadFileHandler = useCallback(async () => {
    if (!fileDto.fileId) return;
    try {
      const fileResponse = await getFileById(fileDto.fileId, {
        onDownloadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / fileDto.fileSizeBytes);
          setDownloadingPercents(percentCompleted);
        },
      });
      downloadFile(fileResponse);
    } catch (error: any) {
      logger().error(error);
    } finally {
      setDownloadingPercents(undefined);
    }
  }, [fileDto.fileId, fileDto.fileSizeBytes]);

  const deleteHandler = useCallback(async () => {
    if (!fileDto.fileId) return;
    const result = await open({
      title: t('RemoveFileConfirmationModal.Title'),
      text: (
        <span className={styles.modalText}>
          <Trans
            t={t}
            i18nKey={'RemoveFileConfirmationModal.Text'}
            values={{ fileName: fileDto.fileName }}
            components={{
              Bold: <span className={styles.boldText} />,
              Red: <span className={styles.redText} />,
            }}
          />
        </span>
      ),
      cancelButtonText: t('RemoveFileConfirmationModal.CancelButtonText'),
      okButtonColorSchema: 'destroy',
      okButtonText: t('RemoveFileConfirmationModal.DeleteButtonText'),
    });

    if (result !== ConfirmResult.Confirm) return;

    try {
      setDeleting(true);
      await removeAttachmentFromStudy(id!, fileDto.fileId);
      await client.invalidateQueries(getStudyAttachmentsQueryKey(id!));
      showNotificationToast(
        t('FileHasDeletedToast.Message', { fileName: fileDto.fileName }),
        t('FileHasDeletedToast.Header'),
        NewCommentIcon,
        3000,
      );
    } catch (error: any) {
      logger().error(error);
    } finally {
      setDeleting(false);
    }
  }, [client, fileDto.fileId, fileDto.fileName, id, open, t]);

  const animation = useMemo(() => {
    if (!!downloadingPercents) {
      return (
        <Lottie
          animationData={ProgressAnimation}
          style={{ width: 20, height: 20, flexShrink: 0 }}
          play={false}
          loop={false}
          // ProgressAnimation has 133 frames
          goTo={Math.round(downloadingPercents * 1.33)}
        />
      );
    }

    if (isUploading)
      return (
        <Lottie
          animationData={ProgressAnimation}
          style={{ width: 20, height: 20, flexShrink: 0 }}
          play={false}
          loop={false}
          goTo={Math.round((uploadPercents ?? 0) * 1.33)}
        />
      );

    return null;
  }, [downloadingPercents, isUploading, uploadPercents]);

  return (
    <div>
      <div className={styles.fileComponent}>
        <button disabled={!fileDto.fileId} className={styles.downloadButton} onClick={downloadFileHandler}>
          <div className={styles.fileNameWithIcon}>
            {animation ?? <FileIcon className={styles.fileIcon} />}
            <div className={styles.fileName}>{fileDto.fileName}</div>
          </div>
          <div className={styles.fileSize}>{formatFileSize(fileDto.fileSizeBytes, 0)}</div>
        </button>
        {fileDto.fileId && (
          <AppButtonWithTooltip
            onClick={deleteHandler}
            variant={'icon-link'}
            colorSchema={'basic'}
            Icon={CrossIcon}
            isLoading={deleting}
            tooltip={t('RemoveFileButtonTooltip')}
          />
        )}
      </div>
      {modal}
    </div>
  );
};

const StudyFilesUploadingComponent = () => {
  const { t } = useScopedTranslation('StudyInfoPage.StudyFilesSection');
  const uploadModal = useUploadFileModal();
  const { isLoading, progress } = useFileUploader();

  return (
    <>
      {progress.percents > 0 ? (
        <div className={styles.uploadingBar}>
          <AppProgress value={progress.percents ?? 0} max={100} />
          <div className={styles.percents}>{`${progress.percents}%`}</div>
        </div>
      ) : (
        <AppButtonWithTooltip
          variant={'icon-link'}
          colorSchema={'primary'}
          Icon={AddIcon}
          onClick={uploadModal.open}
          tooltip={t('AddButtonTooltip')}
          isLoading={isLoading}
        />
      )}
      {uploadModal.modal}
    </>
  );
};

const useUploadFileModal = () => {
  const { t } = useScopedTranslation('StudyInfoPage.StudyFilesSection');
  const { id } = useStudy() ?? {};
  const client = useQueryClient();
  const modal = useModal();

  const onComplete = useCallback(
    async (data: IStudyAttachmentDto[]) => {
      await client.invalidateQueries(getStudyAttachmentsQueryKey(id!));
      showNotificationToast(
        data.length > 1
          ? t('FileHasUploadedToast.MessageBatch')
          : t('FileHasUploadedToast.MessageSingle', { fileName: data[0].fileName }),
        t('FileHasUploadedToast.Header'),
        NewCommentIcon,
        3000,
      );
    },
    [client, id, t],
  );

  const beforeUploading = useCallback(() => {
    showNotificationToast(t('StartToUploadFiles.Message'), t('StartToUploadFiles.Header'), NewCommentIcon, 3000);
  }, [t]);

  const { upload, isLoading, progress } = useFileUploader(beforeUploading, onComplete);

  // NOTE: Close modal if upload is in progress
  useEffect(() => {
    if (isLoading && progress.percents > 0) modal.closeModal();
  }, [isLoading, modal, progress.percents]);

  return {
    open: modal.openModal,
    modal: modal.visible && <ModalContent onClose={modal.closeModal} onSubmit={upload} isLoading={isLoading} />,
  };
};

type FormState = {
  files: AppAttachmentValue[];
};

const ModalContent: FC<{
  onClose: () => void;
  onSubmit: (files: AppAttachmentValue[]) => void;
  isLoading?: boolean;
}> = ({ onClose, onSubmit, isLoading }) => {
  const { t } = useScopedTranslation('StudyInfoPage.StudyFilesSection.FileUploadingModal');
  const form = useForm<FormState>({
    criteriaMode: 'all',
  });

  const files = useWatch({ control: form.control, name: 'files' });
  const submit = form.handleSubmit((formData) => {
    onSubmit(formData.files);
  });

  return (
    <DialogModal
      bodyClassName={styles.uploadFileModal}
      visible={true}
      onHide={onClose}
      title={t('Title')}
      isDisabled={isLoading}
      footer={{
        leftButton: {
          text: t('CancelButtonText'),
          onClick: onClose,
          disabled: isLoading,
        },
        rightButton: {
          text: t('UploadButtonText'),
          colorSchema: 'primary',
          onClick: submit,
          disabled: !files?.length,
          isLoading: isLoading,
        },
      }}
    >
      <Controller
        control={form.control}
        name={'files'}
        rules={{ validate: validateFileRule(FILE_TYPES, FILE_SIZE_LIMIT_MB) }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <AppAttachmentsV2
            multiple
            addFileButtonText={t('Content.AddFileButtonText')}
            fileTypes={FILE_TYPES}
            maxFileSizeMb={FILE_SIZE_LIMIT_MB}
            value={mergeValueWithError(value, parseError(error?.message)?.fileErrors)}
            onChange={onChange}
            disabled={isLoading}
          />
        )}
      />
    </DialogModal>
  );
};
